import TrustRating from '@components/views/cards/productCard/trustRating/TrustRating';
import {
	ArticleCardContainer,
	ArticleCardHeading,
	ArticleCardText,
	ArticleCardWrapper,
	ArticleTwoColumn,
} from './articleProductCard.styled';
import { MainProduct, PostTemplate } from 'types/globalTypes';
import { useACF } from '@context/ACFContext';
import RatingList from '@components/views/cards/productCard/ratingList/RatingList';
import DiscountLabel from '@components/views/cards/productCard/discountLabel/DiscountLabel';
import { Button } from '@components/core/button/Button';
import LovedLabel from '@components/views/cards/productCard/lovedLabel/LovedLabel';
import parse from 'html-react-parser';
import { useIsMobile } from '@hooks/useIsMobile';
import { Separator } from '@components/views/cards/productCard/separator/separator.styled';
import ProductImage from '@components/views/cards/productCard/productImage/ProductImage';

interface ArticleProductCardProps {
	title: string;
	paragraph: string;
	cardLink: {
		url?: string;
		title?: string;
		target?: string;
	};
}

const ArticleProductCard: React.FC<ArticleProductCardProps> = ({ title, paragraph, cardLink }) => {
	const { postTemplate } = useACF() as { postTemplate?: PostTemplate };
	const main = postTemplate?.review_page_v4_products_section_settings.main_product;
	const isMobile = useIsMobile();

	if (!postTemplate) return null;

	const mainProduct: MainProduct | undefined = postTemplate.review_page_v4_products_section_settings?.main_product;
	if (!mainProduct) return null;

	const { rating_bar_color, rating_bar_background_color, card_border_color } = mainProduct;

	const {
		trust_pilot_rating_image,
		tr_trust_pilot_review_score_label,
		tr_trust_pilot_excellent_label,
		tr_trust_pilot_customer_reviews_label,
		trust_pilot_rating_score,
	} = mainProduct.trustpilot_component;

	const {
		tr_discount_component_discount_text,
		discount_component_discount_label_background,
		discount_component_discount_label_text_color,
	} = mainProduct.discount_component;

	const { tr_button_text } = mainProduct.button_component;

	if (!main) return null;

	return (
		<ArticleCardWrapper $borderColor={card_border_color}>
			<ArticleCardContainer>
				<ArticleTwoColumn>
					<ProductImage productImage={main.product_image} />
					{isMobile && <Separator $height="1" $spacing="7px" />}
					<TrustRating
						reviewLabel={tr_trust_pilot_review_score_label}
						reviewScore={trust_pilot_rating_score}
						reviewEvaluation={tr_trust_pilot_excellent_label}
						reviewCount={tr_trust_pilot_customer_reviews_label}
						reviewImage={trust_pilot_rating_image}
					/>
				</ArticleTwoColumn>
				{!isMobile && (
					<RatingList
						$barBackground={rating_bar_background_color}
						$barColor={rating_bar_color}
						ratingRepeater={main.rating_repeater}
						prosRepeater={main.product_pros}
						consRepeater={main.product_cons}
						reviewScoreLabel={main.tr_review_score_label}
					/>
				)}
			</ArticleCardContainer>
			<ArticleCardContainer>
				{!isMobile && <ArticleCardHeading>{title}</ArticleCardHeading>}
				{!isMobile && <ArticleCardText>{parse(paragraph)}</ArticleCardText>}
				<DiscountLabel
					text={tr_discount_component_discount_text}
					$textColor={discount_component_discount_label_text_color}
					$backgroundColor={discount_component_discount_label_background}
					$marginBottom="8px"
				/>
				<Button href={cardLink?.url} target={cardLink?.target === '_blank' ? '_blank' : '_self'} primary={true}>
					{tr_button_text}
				</Button>
				<LovedLabel marginTop="18px" />
			</ArticleCardContainer>
		</ArticleCardWrapper>
	);
};

export default ArticleProductCard;
